// 261005 E1-P2 — cột phải của chặng ⑤: khung đọc một tập. Đầu khung (tên + chip + nút) · khối "Cần xem" · thân (kịch bản / tóm tắt).
// Mọi hành động ghi đi qua callback của `ScriptStage` (cùng `writeOne`, cùng hộp hỏi lại như P1).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popconfirm, Space, Tooltip, Typography, message } from 'antd';
import Button from './WbButton';
import type { EpisodeScriptRecord, FilmOutline, ManualSaveResult, OutlineEpisode } from '../../../types/adaptation';
import type { WorkbenchEpisode } from '../../../types/adaptationWorkbench';
import { episodeRowFixBlocker } from '../episodeFixUtils';
import { adaptationAssistantApi } from '../../../services/api/adaptationAssistant';
import { friendlyApiError } from './jobErrorText';
import { newRequestId, pickThreadId } from './assistant/assistantView';
import { formatSourceRanges, humanizeCodes, manualSaveWarnNote } from '../adaptationUtils';
import { REWRITE_WARNING } from '../seasonReaderView';
import ScriptReader, { scrollToAnchor } from './ScriptReader';
import { sceneAnchorId } from './reviewBookView';
import ScriptEditor from './ScriptEditor';
import { useWorkbenchCtx } from './WorkbenchContext';
import WbSlot, { Bang, Pill } from './WbSlot';
import { AssistantRequest, reasonAssistAction, rowEditBlockerCode } from './assistant/assistantIntent';
import OptionsButton from './assistant/OptionsButton';
import type { VisualExtrasApi } from './useVisualExtras';
import VisualExtrasPanel from './VisualExtrasPanel';
import { reasonOptionsRequest } from './assistant/optionsAction';
import { episodeBadges, qualityReviewLine, reviewAction, ReviewReason } from './scriptStageView';
import { buildProposeAcceptBody, plantAcceptErrorText } from './plantAcceptView';
import { outlineHasOverview, overviewReceiptLabel } from './overviewReceiptView';
import CriticReceiptCard from './CriticReceiptCard';
import AutoFixCardView from './AutoFixCardView';
import RejudgeButton, { useRejudgeRun } from './RejudgeButton';
import { rejudgeActionOf } from './rejudgeView';
import { autoFixCardOf } from './autoFixView';
import { episodeMenuOf } from './scriptMenuView';
import { criticLeft, criticSummaryLine, hideCriticFindingsOf, onPageSentence, splitCriticReasons } from './episodeRowView';
import type { EpisodeRowView } from './episodeRowView';
import { scrollWhenReady } from './useScrollWhenReady';

const { Text, Paragraph } = Typography;

const EDITING_WHY = 'Đang sửa tay — Lưu hoặc Huỷ trước';

interface Props {
  /** 261005 E4-P1 — truyện (để lưu sửa tay). */
  storyId: string;
  ep: number;
  row: OutlineEpisode | undefined;
  wbEp: WorkbenchEpisode | undefined;
  script: EpisodeScriptRecord | undefined;
  outline: FilmOutline | null;
  reasons: ReviewReason[];
  /** Số ghi chú không chặn của AI đọc cả mùa ở tập này (`readerNotesFor`). */
  readerNotes: number;
  /** F46: chặng ④/⑤ vẫn đọc tóm tắt bằng tên gốc, kịch bản sẽ thay tên khi viết. */
  originalNameNotice?: string | null;
  locked: boolean;
  /** Có job / việc nền khác ⇒ khoá nút viết. */
  busy: boolean;
  /** Truyện `range`: tập `prev` chưa DRAFT ⇒ không viết được tập này (ca chắc chắn bị backend từ chối). */
  writeBlockedBy: number | null;
  onWriteOne: (ep: number) => void;
  onRewriteRange: (opts: { from: number; to: number; force: true }) => void;
  /** 261005 E2-P2b — nhờ khung Trợ lý (⑤ không còn drawer). */
  onAskAssistant: (r: AssistantRequest) => void;
  onOpenReader: () => void;
  /** 261005 E4-P1 — báo `ScriptStage` có thay đổi sửa tay chưa lưu (để chặn rời). */
  onEditDirty: (dirty: boolean) => void;
  /** Sửa tay đã lưu — `ScriptStage` tải lại dữ liệu. */
  onManualSaved: () => void;
  /** 261005 E5 — mục cấp chặng không nói về tập này (gộp vào cùng danh sách "Cần xem"). */
  stageNeeds?: Array<{ text: string; blocker: boolean; go: () => void }>;
  /** 261012 E1-P4 — người phụ không tên của tập (một instance dùng chung cho cả chặng, do `ScriptStage` tạo). Thiếu ⇒ không hiện panel. */
  visualExtras?: VisualExtrasApi;
  /** 261023 E3-P3a — mở sẵn khối «chưa lên phim» (đến từ Sổ duyệt). */
  openDropped?: boolean;
  /** 3k-a — dòng của tập (chip + câu «máy chưa sửa được» + «Máy đã tự sửa N chỗ») từ `episodeRowView`. */
  rowView?: EpisodeRowView;
  /** 3k-a — mục «Đẩy riêng tập này sang Chapters» trong «⋯». */
  push: { pushable: boolean; pushed: boolean; busy: boolean; reason: string | null };
  onPushOne: (ep: number) => void;
  /** 3k-a — thanh «Người phụ không tên · cả truyện» (nằm trong «Chi tiết kỹ thuật ▸»). */
  techBulkBar?: React.ReactNode;
  /** 3j2b-2 (D8) — «Để máy sửa tập này» (tập đủ điều kiện, bấm được). Vắng ⇒ không nút. */
  autoRepairOne?: { label: string; onClick: () => void; loading: boolean; disabled: boolean };
}

const EpisodeReader: React.FC<Props> = ({
  storyId, ep, row, wbEp, script, outline, reasons, readerNotes, originalNameNotice, locked, busy, writeBlockedBy, onWriteOne, onRewriteRange, onAskAssistant, onOpenReader, onEditDirty, onManualSaved, stageNeeds = [], visualExtras, openDropped, rowView, push, onPushOne, techBulkBar, autoRepairOne,
}) => {
  // Bản ĐÓNG BĂNG lúc bấm "Sửa kịch bản" (mốc CAS) — không đọc từ prop sống vì `refreshAll()` có thể đổi nó khi đang sửa.
  const [editBase, setEditBase] = useState<EpisodeScriptRecord | null>(null);
  const editing = editBase !== null;
  const { setScriptEditing, slots, setTopMenuSource, notifyScriptSaved, refreshAll, workbench, scriptJob, goTo } = useWorkbenchCtx();
  const [techOpen, setTechOpen] = useState(false);   // 3k-a: «Chi tiết kỹ thuật ▸»
  const [criticDetail, setCriticDetail] = useState(false);   // 3k-c lượt 2: «Xem chi tiết» của biên lai giám khảo (mặc định GẬP — toàn thuật ngữ / tiếng Anh)
  const [restoringMeta, setRestoringMeta] = useState(false);
  /** 261015 P2a — "Khôi phục thông tin cảnh": lấy/tạo luồng ⑤ của tập → `propose-restore` (lượt KHÔNG AI + khối READY, xem thử đồng bộ đã chạy) → mở khung Trợ lý để người bấm Áp. */
  const restoreMetadata = async () => {
    setRestoringMeta(true);
    try {
      const params = { storyId, stageKey: 'script' as const, scopeKey: `ep:${ep}` };
      const threads = await adaptationAssistantApi.listThreads(params);
      const tid = pickThreadId(threads, null) ?? (await adaptationAssistantApi.createThread(params)).id;
      await adaptationAssistantApi.proposeRestore(tid, { requestId: newRequestId() });
      onAskAssistant({ episode: ep, issueIds: [] });
      notifyScriptSaved();   // khung Trợ lý đang mở ⇒ tải lại luồng để thấy khối mới
      message.success(`Đã tạo đề xuất khôi phục thông tin cảnh tập ${ep} (không dùng AI) — xem khối trong khung Trợ lý rồi bấm Áp; tập đã đẩy sang Chapters thì đẩy lại.`, 6);
    } catch (e: any) {
      message.error(friendlyApiError(e, 'Không tạo được đề xuất khôi phục'), 6);
    } finally { setRestoringMeta(false); }
  };
  const [acceptingPlant, setAcceptingPlant] = useState(false);
  /** 261015 P2b-3 — "Chấp nhận (cảnh đã có chi tiết)…": cùng khuôn `restoreMetadata` — lấy/tạo luồng ⑤ → `propose-accept-plant` (lượt KHÔNG AI + khối READY, có lý do) → mở khung Trợ lý để NGƯỜI bấm Áp. */
  const acceptPlantWritten = async (beatId: string, reason: string) => {
    setAcceptingPlant(true);
    try {
      const params = { storyId, stageKey: 'script' as const, scopeKey: `ep:${ep}` };
      const threads = await adaptationAssistantApi.listThreads(params);
      const tid = pickThreadId(threads, null) ?? (await adaptationAssistantApi.createThread(params)).id;
      await adaptationAssistantApi.proposeAcceptPlant(tid, buildProposeAcceptBody(newRequestId(), beatId, reason));
      onAskAssistant({ episode: ep, issueIds: [] });
      notifyScriptSaved();
      message.success(`Đã tạo đề xuất chấp nhận chi tiết gieo ${beatId} của tập ${ep} (không dùng AI, không sửa kịch bản) — xem khối trong khung Trợ lý rồi bấm Áp để ghi.`, 6);
    } catch (e: any) {
      message.error(friendlyApiError(e, plantAcceptErrorText(e)), 7);
    } finally { setAcceptingPlant(false); }
  };
  // Khung Trợ lý đọc cờ này để tắt nút ghi của tập đang sửa (kể cả khi EpisodeReader bị remount do đổi tập).
  useEffect(() => {
    setScriptEditing(editing ? ep : null);
    return () => setScriptEditing(null);
  }, [editing, ep, setScriptEditing]);
  const stopEditing = useCallback(() => { setEditBase(null); onEditDirty(false); }, [onEditDirty]);
  const badges = wbEp ? episodeBadges(wbEp) : [];
  const title = wbEp?.title?.trim() || script?.script?.title?.trim() || '';
  const orphan = !!wbEp && !wbEp.inOutline;
  const fixWhy = script ? episodeRowFixBlocker(script) : null;
  const blockerCode = rowEditBlockerCode(script);
  const rejudgeAction = rejudgeActionOf({
    ep: wbEp, script, workbench,
    // Cùng luật với hàng Sổ duyệt: chỉ job viết của PHIÊN này chưa lên workbench (job khác do `rejudgeActionOf` đọc từ `workbench.jobs`) — crawl/localize không khoá.
    lockedReason: editing ? EDITING_WHY : locked ? 'Bước đang khoá' : scriptJob.starting || !!scriptJob.scriptJob ? 'Đang có việc chạy nền — đợi xong rồi thử lại' : null,
  });
  const writeOff = busy || locked || writeBlockedBy !== null || editing;
  const editWhy = fixWhy ?? (locked ? 'Bước đang khoá' : busy ? 'Đang có việc chạy nền — đợi xong rồi sửa' : null);
  const blockedText = writeBlockedBy !== null ? `Cần viết tập ${writeBlockedBy} trước (truyện theo đoạn nguồn viết tuần tự)` : null;
  const hasScript = !!script?.script;
  const qualityLine = qualityReviewLine(script);
  const fixCard = wbEp ? autoFixCardOf(wbEp, script) : null;
  // 261015 P4c-2c-1 (D9): nhãn biên lai tổng quan — thông báo nhẹ, chỉ khi truyện CÓ tổng quan và tập đã có kịch bản.
  const receiptLabel = overviewReceiptLabel(script, outlineHasOverview(outline));
  const rejudgeRun = useRejudgeRun({ storyId, episode: ep, script: { modifiedAt: script?.modifiedAt }, action: rejudgeAction, refreshAll });
  // 3k-a: toolbar còn Sửa · ✦ Trợ lý · ⋯. Phiên bản / Viết lại / Chấm lại / Đẩy riêng / Người phụ LUÔN ở «⋯» (mọi bề ngang). Quyết định mục nào mờ / vì sao: `scriptMenuView.episodeMenuOf`.
  const openExtras = () => { setTechOpen(true); scrollWhenReady('wb-tech-extras', undefined, { block: 'start', flash: true }); };
  const menuSpecs = episodeMenuOf({
    ep, hasScript, versionCount: wbEp?.versionCount ?? 0, editing, locked, writeOff, writeBlockedText: blockedText,
    rejudge: { show: rejudgeAction.show, disabled: !!rejudgeAction.disabled, running: !!rejudgeAction.running || rejudgeRun.starting, tip: rejudgeAction.tip },
    push, extrasMissing: visualExtras ? visualExtras.missing.length : null,
  });
  const menuSig = menuSpecs.map((m) => `${m.key}:${m.label}:${m.disabled ? 1 : 0}:${m.reason ?? ''}`).join('|');
  const handlers = useRef({ onAskAssistant, onWriteOne, onPushOne, run: rejudgeRun.run, openExtras });
  handlers.current = { onAskAssistant, onWriteOne, onPushOne, run: rejudgeRun.run, openExtras };
  useEffect(() => {
    setTopMenuSource('episode', menuSpecs.map((m) => ({
      key: m.key, label: m.label, danger: m.danger, disabled: m.disabled, reason: m.reason,
      onSelect: () => {
        const h = handlers.current;
        if (m.key === 'rewrite') h.onWriteOne(ep);
        else if (m.key === 'versions') h.onAskAssistant({ episode: ep, issueIds: [], openVersions: true });
        else if (m.key === 'rejudge') void h.run();
        else if (m.key === 'pushOne') h.onPushOne(ep);
        else if (m.key === 'extras') h.openExtras();
      },
    })));
    return () => setTopMenuSource('episode', []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuSig, ep]);

  const chip = rowView?.chip ?? null;
  const criticOpen = !!wbEp && criticLeft(wbEp);
  // 3k-a: thẻ «Máy đã thử sửa…» đã nói câu «Giám khảo AI còn lỗi nặng…» (chỉ thông tin, không nút) ⇒ bỏ dòng trùng; các việc có nút (Nhờ AI sửa, Viết lại…) giữ nguyên.
  const shownAll = rowView?.reason ? reasons.filter((r) => r.kind !== 'critic_blocked') : reasons;
  // 3j2b-2: lỗi của giám khảo (tiếng Anh, theo cảnh, kèm «Phương án (tốn AI)») gập sau «Xem chi tiết»; mặc định chỉ câu lý do + nút hành động.
  const { folded, plain: shownReasons } = splitCriticReasons(shownAll, criticOpen);
  const frameShown = !!rowView?.reason && rowView.chip.key !== 'pass' && rowView.chip.key !== 'pushed';
  const autoOneButton = script && autoRepairOne ? (
    <Tooltip title={editing ? EDITING_WHY : undefined}>
      <span><Button size="small" className="wb-autorepair-btn" loading={autoRepairOne.loading} disabled={autoRepairOne.disabled || editing || locked} onClick={autoRepairOne.onClick}>{autoRepairOne.label}</Button></span>
    </Tooltip>
  ) : null;
  const renderReason = (r: ReviewReason, i: number): React.ReactNode => {
    const one = reviewAction(r, ep, 'one');
    const from = reviewAction(r, ep, 'from');
    const rwOne = r.rewrite;
    const rwFrom = r.rewriteFrom;
    const assist = reasonAssistAction(r, ep, blockerCode, locked || editing);
    return (
      <div className="wb-need" key={`${r.kind}-${i}`}>
        <span className="wb-need-t"><Bang />{r.text}</span>
        <Space size={6} wrap>
          {assist && (
            <Tooltip title={assist.disabledReason}>
              <Button size="small" type="default" disabled={!!assist.disabledReason} onClick={() => onAskAssistant(assist.intent)}>{assist.label}</Button>
            </Tooltip>
          )}
          {assist && (
            <OptionsButton disabledReason={assist.disabledReason} onClick={() => onAskAssistant(reasonOptionsRequest(assist.intent, ep, r.text))} />
          )}
          {one?.via === 'writeOne' && (
            <Button size="small" disabled={writeOff} onClick={() => onWriteOne(ep)}>{rwOne?.label ?? `Viết lại tập ${ep}`}</Button>
          )}
          {one?.via === 'openReader' && <Button size="small" onClick={onOpenReader}>Xem</Button>}
          {one?.via === 'range' && rwOne && (
            <Popconfirm
              title={rwOne.label} description={<div style={{ maxWidth: 360 }}>{REWRITE_WARNING}</div>} okText="Viết lại" cancelText="Thôi"
              disabled={busy || locked} onConfirm={() => onRewriteRange(one.opts)}
            >
              <Button size="small" disabled={busy || locked}>{rwOne.label}</Button>
            </Popconfirm>
          )}
          {from?.via === 'range' && rwFrom && (
            <Popconfirm
              title={rwFrom.label} description={<div style={{ maxWidth: 360 }}>{REWRITE_WARNING}</div>} okText="Viết lại" cancelText="Thôi"
              disabled={busy || locked} onConfirm={() => onRewriteRange(from.opts)}
            >
              <Button size="small" disabled={busy || locked}>{rwFrom.label}</Button>
            </Popconfirm>
          )}
        </Space>
      </div>
    );
  };
  return (
    <div className="wb-reader">
      <h1 className="wb-ep-title">
        {title ? `Tập ${ep} · ${title}` : <span className="wb-ep-notitle">Tập {ep} — chưa có tên</span>}
        {chip && <span className={`wb-rchip wb-rchip-${chip.tone} wb-ep-chip`}>{chip.label}</span>}
      </h1>

      {/* 3k-a: máy hết cách ⇒ MỘT câu tiếng người + nút «Viết lại tập n» (không đếm lần, không mã lỗi). Việc cần xem khác vẫn ở khối «Cần xem» bên dưới. */}
      {frameShown && (
        <div className={`wb-gaveup wb-gaveup-${rowView.chip.key}`} role="note" data-testid="gaveup-card">
          <span className="wb-gaveup-t">
            {onPageSentence(rowView.reason)}   {/* ở chính tập này thì không «— mở tập để xem» */}
            {/* Lời giám khảo C2 NGUYÊN VĂN: dời vào khối gập (dòng tập ở bảng chỉ một câu cố định). */}
            {rowView.judge && <details className="wb-gaveup-judge"><summary>Lời giám khảo AI</summary><div className="wb-gaveup-judge-t">{rowView.judge}</div></details>}
          </span>
          {/* 3k-c lượt 4: thứ tự hành động — «Để máy sửa tập này» là nút CHÍNH (nhấn bằng màu nhấn, không đỏ: nút đỏ chỉ ở dải / đáy cột), «Viết lại tập n» là nút thường đặt SAU. */}
          <Space size={8} wrap>
            {autoOneButton}
            {rowView.rewrite && (
              <Tooltip title={editing ? EDITING_WHY : (blockedText ?? undefined)}>
                <span><Button size="small" disabled={writeOff} onClick={() => onWriteOne(ep)}>{rowView.rewrite.label}</Button></span>
              </Tooltip>
            )}
          </Space>
        </div>
      )}

      {/* 3k-c (User 26/10 «thông tin hiện đủ, ít phải mở»): thẻ «Máy đã tự sửa» ĐẦY ĐỦ (chuỗi bước · Xem trước/sau · Hoàn tác · Dùng lại) hiện NGAY dưới tên tập khi máy đã sửa — thay dòng «Xem · Hoàn tác» + hộp riêng của 3k-a. */}
      {script && wbEp && fixCard && (
        <div className="wb-fixcard" data-testid="fixcard">
          <AutoFixCardView card={fixCard} wbEp={wbEp} script={script} outline={outline} storyId={storyId} refreshAll={refreshAll} disabledReason={editing ? EDITING_WHY : editWhy} hideEpChip />
        </div>
      )}

      {/* 3j2b-2 (D8): tập đủ điều kiện ⇒ nút «Để máy sửa tập này» ngay dưới câu lý do (thay cho danh sách lỗi giám khảo, nay gập sau «Xem chi tiết»). Nút thường (nút đỏ chỉ có ở dải / đáy cột). */}
      {script && autoRepairOne && !frameShown && <div className="wb-autorepair-one" data-testid="autorepair-one">{autoOneButton}</div>}

      {/* 3k-c lượt 2 (User: «trang tập bung khối kỹ thuật»): biên lai giám khảo GẬP thành MỘT dòng tóm tắt tiếng Việt + «Xem chi tiết» (chi tiết là tiếng Anh kỹ thuật, tên cảnh gốc). Mặc định chỉ hiện câu lý do + nút hành động + thẻ «Máy đã tự sửa» khi đã NHẬN bản sửa. */}
      {script && wbEp && criticOpen && (
        <div className="wb-critic-open" data-testid="critic-open">
          <div className="wb-critic-sum" data-testid="critic-sum">
            <span>{criticSummaryLine(wbEp, script)}</span>
            <Button type="link" size="small" aria-expanded={criticDetail} onClick={() => setCriticDetail((v) => !v)}>{criticDetail ? 'Ẩn chi tiết' : 'Xem chi tiết'}</Button>
          </div>
          {criticDetail && (
            <>
              {folded.length > 0 && (
                <div className="wb-needs" role="region" aria-label={`Lỗi giám khảo thấy ở tập ${ep}`} data-testid="critic-folded">
                  <div className="wb-needs-h">Giám khảo AI thấy · {folded.length}</div>
                  {folded.map(renderReason)}
                </div>
              )}
              <CriticReceiptCard script={script} trace={script.localizeTrace} onOpenStage={() => goTo({ stage: 'localize' })} onOpenScene={(id) => scrollToAnchor(sceneAnchorId(id))} openDetails />
            </>
          )}
        </div>
      )}

      {/* 261005 E5: nút của tập đang mở nằm ở thanh trên (portal); handler / điều kiện khoá giữ nguyên. 3k-a: chỉ còn «Sửa» ở đây — ✦ Trợ lý ở vỏ, mọi thứ còn lại ở «⋯». */}
      {script && (
        <WbSlot to={slots.topSecondary}>
          <Tooltip title={editing ? 'Đang sửa — dùng thanh Lưu / Huỷ ở cuối' : (editWhy ?? 'Thêm/xoá/sửa cảnh và dòng bằng tay (không gọi AI, lưu thành phiên bản “Sửa tay”)')}>
            <Button
              type={editing ? 'primary' : 'default'} disabled={editing || !!editWhy}
              onClick={() => { if (script) setEditBase(script); }}
            >
              Sửa
            </Button>
          </Tooltip>
        </WbSlot>
      )}

      {shownReasons.length + stageNeeds.length > 0 && (
        <div className="wb-needs" role="region" aria-label={`Cần xem ở tập ${ep}`}>
          <div className="wb-needs-h">Cần xem · {shownReasons.length + stageNeeds.length}</div>
          {stageNeeds.map((n, i) => (
            <div className="wb-need" key={`stage-${i}`}>
              <span className="wb-need-t"><Bang tone={n.blocker ? 'bad' : 'warn'} />{humanizeCodes(n.text)}</span>
              <Button size="small" onClick={n.go}>Đi tới</Button>
            </div>
          ))}
          {shownReasons.map(renderReason)}
        </div>
      )}
      {readerNotes > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            AI đọc cả mùa có {readerNotes} ghi chú ở tập này (không chặn) — <Button size="small" type="link" style={{ padding: 0 }} onClick={onOpenReader}>Xem</Button>
          </Text>
        </div>
      )}

      {orphan && <Paragraph type="secondary">(tập ngoài khung xương)</Paragraph>}

      {hasScript && script ? (
        editBase ? (
          <ScriptEditor
            storyId={storyId} episode={ep} original={editBase} outline={outline}
            onDirtyChange={onEditDirty} onCancel={stopEditing}
            onSaved={(r: ManualSaveResult) => {
              stopEditing();
              const warn = manualSaveWarnNote(r.warnings);   // 261015 P5a (D8): chỉ đếm lỗi thật — điểm CRITIC cũ / thông báo không thành "cảnh báo"
              message.success(`Đã lưu tập ${ep} — phiên bản v${r.versionNo} “Sửa tay”${warn}${r.relink ? `. Đoạn kết đổi — đã kiểm lại nối tập với tập ${r.relink.nextEpisode}` : ''}`);
              onManualSaved();
            }}
            onReload={() => { stopEditing(); onManualSaved(); }}
          />
        ) : (
          <ScriptReader
            script={script} outline={outline} episode={ep} onAskOptions={onAskAssistant}
            optionsDisabledReason={fixWhy ?? (locked ? 'Bước đang khoá' : undefined)}
            onRestoreMetadata={restoreMetadata} restoringMetadata={restoringMeta} openDropped={openDropped}
            onAcceptPlant={acceptPlantWritten} acceptingPlant={acceptingPlant}
            hideCriticFindings={hideCriticFindingsOf(criticOpen, folded.length)}
          />
        )
      ) : (
        <div className="wb-reader-empty">
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {wbEp?.written === 'failed' ? 'Lần viết gần nhất bị hỏng — chưa có kịch bản dùng được.' : 'Tập này chưa viết.'}
          </Paragraph>
          {row && !orphan && (
            <>
              {originalNameNotice && <Paragraph type="secondary" style={{ fontSize: 13 }}>{originalNameNotice}</Paragraph>}
              <Paragraph><Text strong>Tóm tắt: </Text>{row.synopsis}</Paragraph>
              {row.hook && <Paragraph><Text strong>Móc cuối tập: </Text>{row.hook}</Paragraph>}
              {row.suspense && <Paragraph><Text strong>Còn treo: </Text>{row.suspense}</Paragraph>}
              {row.sourceRanges && row.sourceRanges.length > 0 && (
                <Paragraph type="secondary" style={{ fontSize: 13 }}>Nguồn: {formatSourceRanges(row.sourceRanges)}</Paragraph>
              )}
            </>
          )}
          {!orphan && (
            <Space direction="vertical" size={4}>
              <Tooltip title={blockedText ?? undefined}>
                <Button type="primary" disabled={writeOff} onClick={() => onWriteOne(ep)}>
                  {wbEp?.written === 'failed' || script ? `Viết lại tập ${ep}` : `Viết tập ${ep}`}
                </Button>
              </Tooltip>
              {blockedText && <Text type="secondary" style={{ fontSize: 13 }}>{blockedText}</Text>}
              <Text type="secondary" style={{ fontSize: 13 }}>Gọi AI (tốn AI)</Text>
            </Space>
          )}
        </div>
      )}

      {/* 3k-a → 3k-c: CHỈ phần thật sự kỹ thuật gập sau «Chi tiết kỹ thuật ▸» (không xoá gì): biên lai tự kiểm lúc viết · thẻ giám khảo (khi tập sạch) · mô hình · tổng quan · người phụ không tên · chấm lại bằng AI. Thẻ «Máy đã tự sửa» và danh sách lỗi giám khảo của tập lỗi đã ra ngoài. */}
      {script && (
        <div className="wb-tech" data-tour="tech">
          <button type="button" className="wb-tech-h" aria-expanded={techOpen} onClick={() => setTechOpen((v) => !v)}>Chi tiết kỹ thuật {techOpen ? '▾' : '▸'}</button>
          {techOpen && (
            <div className="wb-tech-b" data-testid="tech-body">
              {((script.script?.scenes.length ?? 0) > 0 || badges.length > 0) && (
                <div className="wb-ep-meta">
                  <Pill tone="todo">{script.script?.scenes.length ?? 0} cảnh</Pill>
                  {badges.map((b) => <Pill key={b.key} tone={b.key === 'chapters' ? 'ok' : 'todo'} title={b.tip}>{b.label}</Pill>)}
                </div>
              )}
              {/* 261015 P4a: biên lai tự kiểm nhịp LÚC VIẾT (không phải trạng thái hiện tại — người có thể đã sửa sau đó). Không hiện khi 0 lỗi / bản cũ. */}
              {qualityLine && <div style={{ marginBottom: 8 }}><Text type="secondary" style={{ fontSize: 13 }}>{qualityLine}</Text></div>}
              {/* 261015 P4b-1: thẻ "Giám khảo" (biên lai C2 lúc SINH tập, chỉ đọc) kèm truy vết bước ③ — bản v1 / không có critic ⇒ không render gì. */}
              {!criticOpen && <CriticReceiptCard script={script} trace={script.localizeTrace} onOpenStage={() => goTo({ stage: 'localize' })} onOpenScene={(id) => scrollToAnchor(sceneAnchorId(id))} />}
              {/* 261023 E3-P3b2: «Chấm lại bằng AI» — giám khảo chấm lại bản đang lưu (không sửa chữ). Cũng có ở «⋯». */}
              {rejudgeAction.show && (
                <div style={{ marginBottom: 8 }} data-testid="rejudge-row">
                  <RejudgeButton storyId={storyId} episode={ep} script={script} action={rejudgeAction} refreshAll={refreshAll} controller={rejudgeRun} />
                </div>
              )}
              {/* 261015 P4c-2c-1 (D9): "viết với tổng quan nào" — biên lai lần SINH tập. Không phải lỗi, không chặn. */}
              {receiptLabel && <div style={{ marginBottom: 8 }} data-testid="overview-receipt"><Text type="secondary" style={{ fontSize: 13 }}>ⓘ {receiptLabel.text}</Text></div>}
              {/* 261012 E1-P4: người phụ không tên — thanh cả truyện + panel của tập. */}
              {(techBulkBar || visualExtras) && (
                <div id="wb-tech-extras" className="wb-tech-extras">
                  {techBulkBar}
                  {visualExtras && <VisualExtrasPanel episode={ep} api={visualExtras} locked={locked} shellBusy={busy} />}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EpisodeReader;
